// Purpose: Convert submission payloads into Firestore-supported shapes (no nested arrays).

type AnyRecord = Record<string, any>;

const isArray = Array.isArray;

const isNestedArrayWrapper = (value: unknown): value is { firestoreNestedArray: true; items: unknown[] } =>
  typeof value === 'object' &&
  value !== null &&
  !isArray(value) &&
  (value as { firestoreNestedArray?: unknown }).firestoreNestedArray === true &&
  isArray((value as { items?: unknown }).items);

/** Align with submissionShape `coerceAnswerCell`: keep 1/0/NA through Firestore encode (numbers were dropped before). */
const answerCellToString = (cell: unknown): string => {
  if (cell == null) return '';
  if (typeof cell === 'string') return cell;
  if (typeof cell === 'number' && Number.isFinite(cell)) return String(cell);
  if (typeof cell === 'boolean') return String(cell);
  return String(cell);
};

const to1D = (v: unknown): string[] => {
  if (isNestedArrayWrapper(v)) return (v.items ?? []).map(answerCellToString);
  return isArray(v) ? v.map(answerCellToString) : [];
};

const to2D = (v: unknown): string[][] => {
  if (!isArray(v)) return [];
  if (isArray(v[0]) || isNestedArrayWrapper(v[0])) return v.map((row) => to1D(row));
  return [to1D(v)];
};

/** Convert nested array (e.g. string[][]) to Firestore-safe format. Firestore rejects arrays of arrays. */
const toFirestoreSafeNestedArray = (v: unknown): unknown => {
  if (!isArray(v)) return v;
  const first = v[0];
  if (Array.isArray(first) || isNestedArrayWrapper(first)) {
    return v.map((row) => ({
      firestoreNestedArray: true as const,
      items: to1D(row),
    }));
  }
  return v;
};

export type FirestoreChart = {
  index: number;
  title: string;
  answers: string[];
  focus: string[];
  treatments: string[];
};

export const toFirestoreSafeSubmissionPayload = <T extends AnyRecord>(payload: T): T => {
  const next: AnyRecord = { ...payload };
  // Firestore rejects nested arrays. Convert problems, dates, shifts (string[][]) to safe format.
  if (Array.isArray(payload.problems)) next.problems = toFirestoreSafeNestedArray(payload.problems);
  if (Array.isArray(payload.dates)) next.dates = toFirestoreSafeNestedArray(payload.dates);
  if (Array.isArray(payload.shifts)) next.shifts = toFirestoreSafeNestedArray(payload.shifts);
  if (Array.isArray(payload.nutritionAnswers)) {
    next.nutritionAnswers = toFirestoreSafeNestedArray(payload.nutritionAnswers);
  }

  const answers2D = to2D(payload.answers);
  // focus and treatments are 1D arrays (one value per chart), convert to 2D by wrapping each element
  const focus1D = to1D(payload.focus);
  const treatments1D = to1D(payload.treatments);
  const chartCount = answers2D.length;
  // Ensure focus2D and treatments2D have the same length as chartCount, padding with empty arrays if needed
  const focus2D = Array.from({ length: chartCount }, (_, i) => 
    i < focus1D.length ? [focus1D[i]] : []
  );
  const treatments2D = Array.from({ length: chartCount }, (_, i) => 
    i < treatments1D.length ? [treatments1D[i]] : []
  );
  const titles = to1D(payload.chartTitles);

  if ((!payload.answers && isArray(payload.charts)) || answers2D.length === 0) return next as T;

  const charts: FirestoreChart[] = answers2D.map((answersRow, i) => ({
    index: i,
    title: titles[i] ?? `Chart ${i + 1}`,
    answers: answersRow,
    focus: focus2D[i] ?? [],
    treatments: treatments2D[i] ?? [],
  }));

  next.charts = charts;
  delete next.answers;
  delete next.focus;
  delete next.treatments;
  delete next.chartTitles;

  return next as T;
};
